import React, { useState } from 'react';
import {
  RefreshIcon,
  AlertIcon,
  CheckCircleIcon,
  BoxIcon,
  ArrowUpIcon,
} from '../components/common/Icons';
import { UploadCloudIcon } from '../components/common/Icons';
import { useAuth } from '../contexts/AuthContext';
import { useUpgradeDashboard } from '../features/upgrades/hooks/useUpgradeDashboard';
import { UpgradeHistoryItem } from '../features/upgrades/types';
import VersionRegistryPanel from '../features/upgrades/components/VersionRegistryPanel';
import { BatchUpgradeModal } from '../features/containers/components/BatchUpgradeModal';
import { useBatchUpgrade } from '../features/containers/hooks/useBatchUpgrade';
import { fetchContainers } from '../features/containers/api/containerApi';
import { fetchVersions } from '../features/upgrades/api/upgradeApi';
import type { ContainerStatus } from '../features/containers/types';

// ============================================================================
// Stat Card (matches UsageDashboardPage pattern)
// ============================================================================

interface StatCardProps {
  label: string;
  value: string;
  subValue?: string;
  icon: React.ReactNode;
  alert?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, subValue, icon, alert }) => (
  <div className={`stat-card ${alert ? 'stat-card-alert' : ''}`}>
    <div className="stat-card-icon">{icon}</div>
    <div className="stat-card-content">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
      {subValue && <div className="stat-card-subvalue">{subValue}</div>}
    </div>
  </div>
);

// ============================================================================
// Status Badge Helper
// ============================================================================

function upgradeStatusColor(status: string): string {
  switch (status) {
    case 'completed': return 'active';
    case 'failed': return 'suspended';
    case 'rolled_back': return 'archived';
    case 'pending':
    case 'validating':
    case 'backing_up':
    case 'stopping':
    case 'migrating':
    case 'updating_compose':
    case 'starting':
    case 'validating_post':
      return 'provisioning';
    case 'rolling_back':
      return 'archived';
    default: return 'provisioning';
  }
}

// ============================================================================
// Upgrade History Table
// ============================================================================

const UpgradeHistoryTable: React.FC<{ items: UpgradeHistoryItem[] }> = ({ items }) => (
  <div className="table-container">
    <table className="data-table">
      <thead>
        <tr>
          <th>Tenant</th>
          <th>From</th>
          <th>To</th>
          <th>Status</th>
          <th>Started</th>
          <th>Initiated By</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <td>{item.tenant_id}</td>
            <td>{item.from_version}</td>
            <td>{item.to_version}</td>
            <td>
              <span className={`status-badge status-${upgradeStatusColor(item.status)}`}>
                {item.status.replace(/_/g, ' ')}
              </span>
            </td>
            <td>{item.started_at ? new Date(item.started_at).toLocaleString() : '—'}</td>
            <td>{item.initiated_by || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

const UpgradesPage: React.FC = () => {
  const { authFetch } = useAuth();
  const { dashboard, loading, error, lastRefresh, refresh } = useUpgradeDashboard();
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchContainers, setBatchContainers] = useState<ContainerStatus[]>([]);
  const [batchVersions, setBatchVersions] = useState<string[]>([]);
  const { initiateBatch, progress, cancelBatch, isRunning } = useBatchUpgrade(authFetch);

  const handleOpenBatch = async () => {
    try {
      const [containerData, versionData] = await Promise.all([
        fetchContainers(authFetch),
        fetchVersions(),
      ]);
      setBatchContainers(containerData);
      setBatchVersions(
        versionData.versions
          .map((v) => v.version)
          .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
      );
    } catch {
      setBatchContainers([]);
      setBatchVersions([]);
    }
    setShowBatchModal(true);
  };

  return (
    <div className="page-container upgrades-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1><UploadCloudIcon size={24} /> Upgrades</h1>
          <p className="page-description">
            Manage platform versions and tenant upgrade operations
          </p>
        </div>
        <div className="page-header-actions" style={{ gap: '12px' }}>
          <span className="last-refresh">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            className="btn btn-secondary"
            onClick={refresh}
            disabled={loading}
          >
            <RefreshIcon size={16} /> Refresh
          </button>
          {dashboard && dashboard.tenants_needing_upgrade > 0 && (
            <button
              className="btn btn-primary"
              onClick={handleOpenBatch}
            >
              <ArrowUpIcon size={16} /> Batch Upgrade
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-error">
          <AlertIcon size={18} />
          <span>{error}</span>
          <button className="btn btn-sm" onClick={refresh}>Retry</button>
        </div>
      )}

      {/* Loading State */}
      {loading && !dashboard && (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading upgrade dashboard...</p>
        </div>
      )}

      {/* Content */}
      {dashboard && (
        <>
          {/* Stats Cards */}
          <section className="usage-section">
            <h2>Overview</h2>
            <div className="stats-grid stats-grid-3">
              <StatCard
                label="Total Versions"
                value={dashboard.total_versions.toString()}
                subValue={`${dashboard.stable_versions} stable`}
                icon={<BoxIcon size={24} />}
              />
              <StatCard
                label="Latest Version"
                value={dashboard.latest_version || 'None'}
                icon={<ArrowUpIcon size={24} />}
              />
              <StatCard
                label="Tenants Needing Upgrade"
                value={dashboard.tenants_needing_upgrade.toString()}
                subValue={`of ${dashboard.total_container_tenants} tenants`}
                icon={<AlertIcon size={24} />}
                alert={dashboard.tenants_needing_upgrade > 0}
              />
            </div>
          </section>

          {/* Version Registry */}
          <section className="usage-section">
            <VersionRegistryPanel />
          </section>

          {/* Recent Upgrades */}
          <section className="usage-section">
            <h2>Recent Activity</h2>
            {dashboard.recent_upgrades.length > 0 ? (
              <UpgradeHistoryTable items={dashboard.recent_upgrades} />
            ) : (
              <div className="empty-state">
                <CheckCircleIcon size={48} />
                <p>No recent upgrade activity</p>
              </div>
            )}
          </section>
        </>
      )}

      {showBatchModal && (
        <BatchUpgradeModal
          containers={batchContainers}
          availableVersions={batchVersions}
          onClose={() => {
            if (!isRunning) {
              setShowBatchModal(false);
              refresh();
            }
          }}
          onStart={async (tenantIds, targetVersion, parallelLimit) => {
            await initiateBatch({
              tenant_ids: tenantIds,
              target_version: targetVersion,
              batch_size: parallelLimit,
            });
          }}
          onCancel={async () => {
            await cancelBatch();
          }}
          progress={progress}
          isRunning={isRunning}
        />
      )}
    </div>
  );
};

export default UpgradesPage;
